import { useMemo, useState } from 'react'
import {
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import DateObject from 'react-date-object'
import persian from 'react-date-object/calendars/persian'
import persian_fa from 'react-date-object/locales/persian_fa'
import DatePicker from 'react-multi-date-picker'

import {
  AutoStatsRover,
  RoverReport,
  RoverReportBase,
  RoverReportSession,
  useAutoStatsRoverReport,
} from 'api'
import { strings } from 'strings'

import {
  computeSpecFromKeys,
  formatDayLabel,
  formatJalaliDateTime,
  shiftDayKey,
  tehranDayKeyOf,
  toPersianDigits,
} from './analyticsShared'

interface Props {
  open: boolean
  onClose: () => void
  rover: AutoStatsRover | null
  from: string
  to: string
  showDatePicker?: boolean
}

export function RoverReportDialog({
  open,
  onClose,
  rover,
  from,
  to,
  showDatePicker,
}: Props) {
  const classes = useStyles()
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{ style: { direction: 'rtl' } }}
    >
      <DialogTitle className={classes.dialogTitle}>
        {rover
          ? `${strings.roverActivityReport} — ${rover.displayName?.trim() || rover.username}`
          : strings.roverActivityReport}
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        {open && rover && (
          <ReportBody
            rover={rover}
            from={from}
            to={to}
            showDatePicker={showDatePicker}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small" variant="outlined">
          {strings.close}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function dayKeyToDateObject(key: string): DateObject {
  const [y, m, d] = key.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  dt.setHours(0, 0, 0, 0)
  return new DateObject(dt).convert(persian, persian_fa)
}

function ReportBody({
  rover,
  from,
  to,
  showDatePicker,
}: {
  rover: AutoStatsRover
  from: string
  to: string
  showDatePicker?: boolean
}) {
  const classes = useStyles()
  const [range, setRange] = useState<[DateObject, DateObject]>(() => [
    dayKeyToDateObject(from),
    dayKeyToDateObject(to),
  ])
  const fromKey = showDatePicker ? tehranDayKeyOf(range[0].toDate()) : from
  const toKey = showDatePicker ? tehranDayKeyOf(range[1].toDate()) : to
  const { report, loading, error } = useAutoStatsRoverReport(
    rover.userId,
    fromKey,
    toKey,
  )

  const dateBar = showDatePicker ? (
    <div className={classes.rangeBar}>
      <DatePicker
        range
        calendar={persian}
        locale={persian_fa}
        digits={['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']}
        value={range}
        onChange={(value) => {
          if (Array.isArray(value) && value.length === 2) {
            setRange(value as [DateObject, DateObject])
          }
        }}
        format="YYYY/MM/DD"
        maxDate={new Date()}
        render={(value, openCalendar) => (
          <TextField
            variant="outlined"
            size="small"
            label={`${strings.from} - ${strings.to}`}
            value={value as string}
            onClick={openCalendar}
            InputProps={{ readOnly: true }}
          />
        )}
      />
    </div>
  ) : null

  let content
  if (loading) {
    content = (
      <div className={classes.status}>
        <CircularProgress size={24} />
        <Typography>{strings.loading}</Typography>
      </div>
    )
  } else if (error) {
    content = (
      <Typography className={classes.status}>
        {strings.error}: {String((error as any)?.message ?? error)}
      </Typography>
    )
  } else if (!report) {
    content = (
      <Typography className={classes.status}>
        {strings.noSessionsInRange}
      </Typography>
    )
  } else {
    content = (
      <>
        <SummaryHeader report={report} />
        <DailyActivitySection report={report} from={fromKey} to={toKey} />
        <BaseSummarySection report={report} />
      </>
    )
  }

  return (
    <div className={classes.bodyWrap}>
      {dateBar}
      {content}
    </div>
  )
}

function SummaryHeader({ report }: { report: RoverReport }) {
  const classes = useStyles()
  return (
    <div className={classes.summary}>
      <div className={classes.summaryHead}>
        <Typography className={classes.summaryName}>
          {report.displayName?.trim() || report.username}
        </Typography>
      </div>
      <div className={classes.summaryGrid}>
        <SummaryStat
          label={strings.username}
          hint={strings.hintRoverUsername}
          value={report.username}
          ltr
        />
        <SummaryStat
          label={strings.phoneNumber}
          hint={strings.hintRoverPhone}
          value={report.phoneNumber || '—'}
          ltr
        />
        <SummaryStat
          label={strings.totalConnections}
          hint={strings.hintRoverTotalConnections}
          value={toPersianDigits(report.totalConnections)}
        />
        <SummaryStat
          label={strings.totalAirtime}
          hint={strings.hintRoverTotalAirtime}
          value={formatDuration(report.totalDurationSeconds)}
        />
        <SummaryStat
          label={strings.maxDuration}
          hint={strings.hintRoverMaxDuration}
          value={formatDuration(report.maxSessionDurationSeconds)}
        />
      </div>
    </div>
  )
}

function SummaryStat({
  label,
  hint,
  value,
  ltr,
}: {
  label: string
  hint?: string
  value: string
  ltr?: boolean
}) {
  const classes = useStyles()
  return (
    <div className={classes.statBox}>
      <div className={classes.statLabelRow}>
        <Typography className={classes.statLabel}>{label}</Typography>
        {hint && <HintIcon text={hint} />}
      </div>
      <Typography
        className={classes.statValue}
        style={ltr ? { direction: 'ltr', textAlign: 'right' } : undefined}
      >
        {value}
      </Typography>
    </div>
  )
}

function HintIcon({ text }: { text: string }) {
  const classes = useStyles()
  return (
    <Tooltip title={text} arrow placement="top">
      <InfoOutlinedIcon className={classes.hintIcon} fontSize="small" />
    </Tooltip>
  )
}

interface DayActivity {
  dayKey: string
  dayStartSec: number
  dayEndSec: number
  // Sum of session durationSeconds attributed to this day. May exceed 86400
  // when the rover holds concurrent sessions to multiple mountpoints — the
  // backend's totalDurationSeconds is computed the same way (durations summed
  // across overlapping sessions), so this matches.
  totalSeconds: number
  // 24 hour-of-day airtime buckets, in seconds. Each value is the sum of
  // session airtime allocated to that hour; concurrent sessions can push a
  // single bucket above 3600.
  hourBuckets: number[]
}

// Convert a Tehran-local dayKey ("YYYY-MM-DD") to the unix-second timestamp
// of midnight in Tehran. We compute the offset from a UTC-equivalent of the
// same wall-clock parts using Intl, so DST transitions (none in Iran since
// 2022, but kept correct in case that changes) are handled correctly.
const tehranOffsetFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Tehran',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

function tehranOffsetMs(at: Date): number {
  const parts = tehranOffsetFmt.formatToParts(at)
  const get = (t: string): number =>
    Number(parts.find((p) => p.type === t)?.value ?? 0)
  // `hour` may come back as "24" right at midnight; normalize to 0 of next day.
  let h = get('hour')
  let dayShift = 0
  if (h === 24) {
    h = 0
    dayShift = 1
  }
  const wallAsUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day') + dayShift,
    h,
    get('minute'),
  )
  return wallAsUtc - at.getTime()
}

function tehranDayStartUnix(dayKey: string): number {
  const [y, m, d] = dayKey.split('-').map(Number)
  const naiveMs = Date.UTC(y, m - 1, d)
  const offsetMs = tehranOffsetMs(new Date(naiveMs))
  return Math.floor((naiveMs - offsetMs) / 1000)
}

function tehranDayHourOf(unixSec: number): { day: string; hour: number } {
  const parts = tehranOffsetFmt.formatToParts(new Date(unixSec * 1000))
  const get = (t: string): string =>
    parts.find((p) => p.type === t)?.value ?? '0'
  let h = Number(get('hour'))
  let day = `${get('year')}-${get('month')}-${get('day')}`
  if (h === 24) {
    h = 0
    day = shiftDayKey(day, 1)
  }
  return { day, hour: h }
}

function buildDailyActivity(
  sessions: RoverReportSession[],
  fromKey: string,
  toKey: string,
): DayActivity[] {
  const spec = computeSpecFromKeys(fromKey, toKey)
  const days: DayActivity[] = spec.keys.map((k) => {
    const startSec = tehranDayStartUnix(k)
    const endSec = tehranDayStartUnix(shiftDayKey(k, 1))
    return {
      dayKey: k,
      dayStartSec: startSec,
      dayEndSec: endSec,
      totalSeconds: 0,
      hourBuckets: new Array(24).fill(0) as number[],
    }
  })
  if (days.length === 0) return days

  for (const s of sessions) {
    // The session record's [connectUnix, disconnectUnix] window is wider than
    // the actual streaming time — disconnectUnix-connectUnix routinely exceeds
    // durationSeconds by minutes (and totalDurationSeconds across both API
    // endpoints equals the sum of durationSeconds, not of windows). So treat
    // durationSeconds as the source of truth and distribute it across hour
    // buckets using the connect/disconnect window as the placement hint.
    const dur = s.durationSeconds
    let segStart: number
    let segEnd: number
    let scale: number
    if (
      s.connectUnix > 0 &&
      s.disconnectUnix > s.connectUnix &&
      dur > 0 &&
      dur <= s.disconnectUnix - s.connectUnix
    ) {
      // Spread duration uniformly over the recorded window.
      segStart = s.connectUnix
      segEnd = s.disconnectUnix
      scale = dur / (segEnd - segStart)
    } else if (s.connectUnix > 0 && dur > 0) {
      // Window degenerate (disconnect=0, disconnect<connect, or duration>window):
      // anchor airtime at the connect timestamp.
      segStart = s.connectUnix
      segEnd = s.connectUnix + dur
      scale = 1
    } else if (s.disconnectUnix > 0 && dur > 0) {
      // Session started before the window — anchor airtime at the disconnect.
      segStart = s.disconnectUnix - dur
      segEnd = s.disconnectUnix
      scale = 1
    } else {
      continue
    }

    for (const day of days) {
      const oStart = Math.max(segStart, day.dayStartSec)
      const oEnd = Math.min(segEnd, day.dayEndSec)
      if (oEnd <= oStart) continue
      const startOff = oStart - day.dayStartSec
      const endOff = oEnd - day.dayStartSec
      const startHour = Math.floor(startOff / 3600)
      const endHour = Math.min(23, Math.floor((endOff - 1) / 3600))
      for (let h = startHour; h <= endHour; h++) {
        const hStart = h * 3600
        const hEnd = hStart + 3600
        const overlap = Math.min(endOff, hEnd) - Math.max(startOff, hStart)
        if (overlap > 0) {
          const add = overlap * scale
          day.hourBuckets[h] += add
          day.totalSeconds += add
        }
      }
    }
  }
  return days
}

function longestActiveStreak(days: DayActivity[]): number {
  let best = 0
  let cur = 0
  for (const d of days) {
    if (d.totalSeconds > 0) {
      cur += 1
      if (cur > best) best = cur
    } else {
      cur = 0
    }
  }
  return best
}

function DailyActivitySection({
  report,
  from,
  to,
}: {
  report: RoverReport
  from: string
  to: string
}) {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(true)

  const days = useMemo(
    () => buildDailyActivity(report.sessions ?? [], from, to),
    [report, from, to],
  )

  if (days.length === 0) return null

  const activeDays = days.filter((d) => d.totalSeconds > 0).length
  const streak = longestActiveStreak(days)

  const toggle = (): void => setExpanded((v) => !v)

  return (
    <section className={classes.section}>
      <div
        className={classes.sectionHeadToggle}
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggle()
          }
        }}
      >
        <div className={classes.dailyTitleWrap}>
          <Typography className={classes.sectionTitle}>
            {strings.dailyActivityTitle}
          </Typography>
          <HintIcon text={strings.hintDailyActivity} />
        </div>
        <IconButton size="small" aria-label={expanded ? 'collapse' : 'expand'}>
          <ExpandMoreIcon
            className={`${classes.expandIcon} ${
              expanded ? classes.expandIconOpen : ''
            }`}
          />
        </IconButton>
      </div>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div className={classes.dailyBody}>
          <div className={classes.dailyStats}>
            <SummaryStat
              label={strings.activeDays}
              hint={strings.hintActiveDays}
              value={`${toPersianDigits(activeDays)} / ${toPersianDigits(
                days.length,
              )}`}
            />
            <SummaryStat
              label={strings.longestStreak}
              hint={strings.hintLongestStreak}
              value={`${toPersianDigits(streak)} ${strings.day}`}
            />
          </div>
          <ActivityHeatmap days={days} />
          <PeakHourStrip report={report} />
        </div>
      </Collapse>
    </section>
  )
}

function PeakHourStrip({ report }: { report: RoverReport }) {
  const classes = useStyles()
  const hourly = report.hourlyUsage
  if (!hourly || hourly.length === 0) return null

  const counts = new Array(24).fill(0) as number[]
  for (const b of hourly) {
    if (b.hour >= 0 && b.hour < 24) counts[b.hour] = b.connections
  }
  const total = counts.reduce((a, b) => a + b, 0)
  if (total === 0) return null

  const max = Math.max(report.peakHourCount ?? 0, ...counts)
  const peakHour =
    report.peakHour !== undefined ? report.peakHour : counts.indexOf(max)
  const peakCount = report.peakHourCount ?? max

  // For each hour-of-day, find the day in the range with the most connections
  // landing in that hour. Used to surface the "peak day" alongside the peak
  // hour and to enrich per-bar tooltips.
  const busiestDayByHour: string[] = new Array(24).fill('')
  if (report.sessions && report.sessions.length > 0) {
    const buckets: Map<string, number>[] = []
    for (let i = 0; i < 24; i++) buckets.push(new Map())
    for (const s of report.sessions) {
      if (s.connectUnix <= 0) continue
      const { day, hour } = tehranDayHourOf(s.connectUnix)
      const m = buckets[hour]
      m.set(day, (m.get(day) ?? 0) + 1)
    }
    for (let h = 0; h < 24; h++) {
      let best = ''
      let bestCount = 0
      buckets[h].forEach((count, day) => {
        if (count > bestCount) {
          best = day
          bestCount = count
        }
      })
      busiestDayByHour[h] = best
    }
  }
  const peakDay = busiestDayByHour[peakHour] ?? ''

  return (
    <div className={classes.peakWrap}>
      <div className={classes.peakHeader}>
        <Typography className={classes.peakHeaderText}>
          {strings.peakHourLabel} {toPersianDigits(peakHour)}:۰۰ —{' '}
          {toPersianDigits(peakCount)} {strings.connectionUnit}
          {peakDay && ` — ${formatDayLabel(peakDay)}`}
        </Typography>
        <HintIcon text={strings.hintPeakHourStrip} />
      </div>
      <div className={classes.peakBars} style={{ direction: 'ltr' }}>
        {counts.map((c, h) => {
          const ratio = max > 0 ? c / max : 0
          const isPeak = h === peakHour && c > 0
          const day = busiestDayByHour[h]
          const tip =
            c > 0
              ? `${toPersianDigits(h)}:۰۰–${toPersianDigits(
                  h + 1,
                )}:۰۰ — ${toPersianDigits(c)} ${strings.connectionUnit}${
                  day ? ` — ${formatDayLabel(day)}` : ''
                }`
              : `${toPersianDigits(h)}:۰۰ — ${strings.noActivityDay}`
          return (
            <Tooltip key={h} title={tip} arrow>
              <div className={classes.peakBarTrack}>
                <div
                  className={`${classes.peakBar} ${
                    isPeak ? classes.peakBarMax : ''
                  }`}
                  style={{
                    height: c > 0 ? `${Math.max(6, ratio * 100)}%` : 0,
                  }}
                />
              </div>
            </Tooltip>
          )
        })}
      </div>
      <div className={classes.peakAxis} style={{ direction: 'ltr' }}>
        {[0, 4, 8, 12, 16, 20].map((h) => (
          <span
            key={h}
            className={classes.peakAxisLabel}
            style={{
              left: `${(h / 24) * 100}%`,
              transform: 'translateX(-50%)',
            }}
          >
            {toPersianDigits(h)}
          </span>
        ))}
      </div>
    </div>
  )
}

function ActivityHeatmap({ days }: { days: DayActivity[] }) {
  const classes = useStyles()
  // Cells max out at 3600s (a fully-active hour). Concurrent sessions can
  // push a bucket past 3600 — we still cap intensity at 1.0 since "more than
  // 60 minutes of airtime in a 60-minute hour" has no extra visual signal,
  // but the tooltip surfaces the raw value.
  const maxPerHour = 3600
  const hourTicks = [0, 3, 6, 9, 12, 15, 18, 21]
  return (
    <div className={classes.heatmapWrap}>
      <div className={classes.heatmapHeaderRow}>
        <div className={classes.heatmapDayLabelSpacer} />
        <div
          className={classes.heatmapHourHeader}
          // Inline `direction` so jss-rtl (project-wide RTL plugin) doesn't
          // flip it back to rtl and reverse the timeline.
          style={{ direction: 'ltr' }}
        >
          {hourTicks.map((h) => (
            <span
              key={h}
              className={classes.heatmapHourTick}
              // Inline transform for the same reason — jss-rtl negates
              // translateX values pulled from makeStyles.
              style={{
                left: `${(h / 24) * 100}%`,
                transform: 'translateX(-50%)',
              }}
            >
              {toPersianDigits(h)}
            </span>
          ))}
        </div>
      </div>
      <div className={classes.heatmapBody}>
        {days.map((day) => (
          <div key={day.dayKey} className={classes.heatmapRow}>
            <span
              className={`${classes.heatmapDayLabel} ${
                day.totalSeconds <= 0 ? classes.heatmapDayLabelDim : ''
              }`}
            >
              {formatDayLabel(day.dayKey)}
            </span>
            <div className={classes.heatmapCells} style={{ direction: 'ltr' }}>
              {day.hourBuckets.map((sec, h) => {
                const intensity =
                  sec > 0 ? Math.max(0.18, Math.min(1, sec / maxPerHour)) : 0
                const tip =
                  sec > 0
                    ? `${formatDayLabel(day.dayKey)} ${toPersianDigits(
                        h,
                      )}:۰۰–${toPersianDigits(h + 1)}:۰۰ — ${formatDuration(sec)}`
                    : `${formatDayLabel(day.dayKey)} ${toPersianDigits(
                        h,
                      )}:۰۰ — ${strings.noActivityDay}`
                return (
                  <Tooltip key={h} title={tip} arrow>
                    <div
                      className={classes.heatmapCell}
                      style={{
                        backgroundColor:
                          intensity > 0
                            ? `rgba(25, 118, 210, ${intensity})`
                            : undefined,
                      }}
                    />
                  </Tooltip>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function BaseSummarySection({ report }: { report: RoverReport }) {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(true)

  // Prefer the server-provided aggregate, fall back to deriving from sessions.
  const aggregates = useMemo<RoverReportBase[]>(() => {
    const provided = report.basesConnectedTo
    if (provided && provided.length > 0) {
      return [...provided].sort(
        (a, b) =>
          b.connections - a.connections ||
          b.totalDurationSeconds - a.totalDurationSeconds,
      )
    }
    return aggregateFromSessions(report.sessions ?? [])
  }, [report])

  if (aggregates.length === 0) return null

  const toggle = () => setExpanded((v) => !v)

  return (
    <section className={classes.section}>
      <div
        className={classes.sectionHeadToggle}
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggle()
          }
        }}
      >
        <Typography className={classes.sectionTitle}>
          {strings.basesConnectedToTitle} ({toPersianDigits(aggregates.length)})
        </Typography>
        <IconButton size="small" aria-label={expanded ? 'collapse' : 'expand'}>
          <ExpandMoreIcon
            className={`${classes.expandIcon} ${
              expanded ? classes.expandIconOpen : ''
            }`}
          />
        </IconButton>
      </div>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div className={classes.baseGrid}>
          {aggregates.map((b) => (
            <div key={b.mountpoint} className={classes.baseCard}>
              <Typography className={classes.baseName}>
                {b.mountpoint}
              </Typography>
              <div className={classes.baseStats}>
                <BaseStat
                  label={strings.connectionsCount}
                  hint={strings.hintBaseConnectionsCount}
                  value={toPersianDigits(b.connections)}
                />
                <BaseStat
                  label={strings.totalAirtime}
                  hint={strings.hintBaseTotalAirtime}
                  value={formatDuration(b.totalDurationSeconds)}
                />
                <BaseStat
                  label={strings.maxDuration}
                  hint={strings.hintBaseMaxDuration}
                  value={formatDuration(b.maxDurationSeconds)}
                />
                {b.lastConnectUnix !== undefined && b.lastConnectUnix > 0 && (
                  <BaseStat
                    label={strings.lastConnect}
                    hint={strings.hintBaseLastConnect}
                    value={formatJalaliDateTime(b.lastConnectUnix)}
                    ltr
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </Collapse>
    </section>
  )
}

function BaseStat({
  label,
  hint,
  value,
  ltr,
}: {
  label: string
  hint?: string
  value: string
  ltr?: boolean
}) {
  const classes = useStyles()
  return (
    <div className={classes.baseStatRow}>
      <span className={classes.baseStatLabel}>
        {label}:{hint && <HintIcon text={hint} />}
      </span>
      <span
        className={classes.baseStatValue}
        style={ltr ? { direction: 'ltr', textAlign: 'left' } : undefined}
      >
        {value}
      </span>
    </div>
  )
}

function aggregateFromSessions(
  sessions: RoverReportSession[],
): RoverReportBase[] {
  const map = new Map<string, RoverReportBase>()
  for (const s of sessions) {
    const cur = map.get(s.mountpoint) ?? {
      mountpoint: s.mountpoint,
      mountpointId: s.mountpointId,
      connections: 0,
      totalDurationSeconds: 0,
      maxDurationSeconds: 0,
      lastConnectUnix: 0,
    }
    cur.connections += 1
    cur.totalDurationSeconds += s.durationSeconds
    if (s.durationSeconds > cur.maxDurationSeconds) {
      cur.maxDurationSeconds = s.durationSeconds
    }
    if (s.connectUnix > (cur.lastConnectUnix ?? 0)) {
      cur.lastConnectUnix = s.connectUnix
    }
    map.set(s.mountpoint, cur)
  }
  return Array.from(map.values()).sort(
    (a, b) =>
      b.connections - a.connections ||
      b.totalDurationSeconds - a.totalDurationSeconds,
  )
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '—'
  // Round to whole seconds. The API returns fractional seconds and the raw
  // modulo would otherwise leak floating-point noise into the label.
  const total = Math.round(seconds)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n: number): string => (n < 10 ? `0${n}` : String(n))
  return toPersianDigits(`${pad(h)}:${pad(m)}:${pad(s)}`)
}

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    padding: theme.spacing(1, 2),
  },
  dialogContent: {
    padding: 0,
  },
  bodyWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  rangeBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(4),
    justifyContent: 'center',
  },
  summary: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    background: '#fafafa',
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  summaryHead: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  summaryName: {
    fontSize: '1.15rem',
    fontWeight: 700,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: theme.spacing(1),
  },
  statBox: {
    display: 'flex',
    flexDirection: 'column',
  },
  statLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  statLabel: {
    fontSize: '0.8rem',
    color: '#666',
  },
  hintIcon: {
    fontSize: '0.95rem',
    color: '#999',
    cursor: 'help',
    verticalAlign: 'middle',
    marginInlineStart: theme.spacing(0.25),
  },
  statValue: {
    fontSize: '0.95rem',
    fontWeight: 600,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  sectionHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  sectionHeadToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    cursor: 'pointer',
    userSelect: 'none',
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    background: '#f5f5f5',
    border: '1px solid #e0e0e0',
    '&:hover': {
      background: '#eee',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
    },
  },
  expandIcon: {
    transition: 'transform 0.2s ease',
  },
  expandIconOpen: {
    transform: 'rotate(180deg)',
  },
  sectionTitle: {
    fontSize: '1.05rem',
    fontWeight: 700,
  },
  baseGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: theme.spacing(1),
  },
  baseCard: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    padding: theme.spacing(1, 1.5),
    background: '#fff',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  baseName: {
    fontWeight: 700,
    fontSize: '1rem',
    paddingBottom: theme.spacing(0.5),
    borderBottom: '1px solid #eee',
  },
  baseStats: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
  },
  baseStatRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.85rem',
  },
  baseStatLabel: {
    color: '#666',
  },
  baseStatValue: {
    fontWeight: 600,
  },
  dailyTitleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  dailyBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0.5),
  },
  dailyStats: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  heatmapWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    padding: theme.spacing(1),
    background: '#fafafa',
    maxHeight: 420,
    overflowY: 'auto',
  },
  heatmapHeaderRow: {
    display: 'flex',
    alignItems: 'flex-end',
    position: 'sticky',
    top: 0,
    background: '#fafafa',
    paddingBottom: theme.spacing(0.25),
    zIndex: 1,
  },
  heatmapDayLabelSpacer: {
    width: 140,
    flexShrink: 0,
  },
  heatmapHourHeader: {
    flex: 1,
    position: 'relative',
    height: 16,
    // `direction` is set inline on the JSX to bypass jss-rtl flipping.
  },
  heatmapHourTick: {
    position: 'absolute',
    // `transform: translateX(-50%)` is set inline for the same reason.
    fontSize: '0.7rem',
    color: '#888',
  },
  heatmapBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  heatmapRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minHeight: 18,
  },
  heatmapDayLabel: {
    width: 140,
    flexShrink: 0,
    fontSize: '0.75rem',
    color: '#444',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  heatmapDayLabelDim: {
    color: '#aaa',
  },
  heatmapCells: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(24, minmax(0, 1fr))',
    gap: 2,
    // `direction` is set inline so jss-rtl can't flip the grid columns.
  },
  heatmapCell: {
    height: 16,
    borderRadius: 2,
    background: '#eee',
    cursor: 'help',
    '&:hover': {
      filter: 'brightness(1.05)',
      outline: '1px solid rgba(25, 118, 210, 0.5)',
    },
  },
  peakWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    padding: theme.spacing(1, 1.5),
    background: '#fafafa',
  },
  peakHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  peakHeaderText: {
    fontSize: '0.85rem',
    fontWeight: 600,
  },
  peakBars: {
    display: 'grid',
    gridTemplateColumns: 'repeat(24, minmax(0, 1fr))',
    gap: 2,
    height: 56,
    // `direction` is set inline so jss-rtl can't flip the grid columns.
  },
  peakBarTrack: {
    height: '100%',
    display: 'flex',
    alignItems: 'flex-end',
    cursor: 'help',
    borderRadius: 2,
    '&:hover': {
      background: 'rgba(0, 0, 0, 0.04)',
    },
  },
  peakBar: {
    width: '100%',
    background: theme.palette.primary.main,
    borderRadius: 2,
    minHeight: 1,
    transition: 'height 0.15s ease',
  },
  peakBarMax: {
    background: theme.palette.primary.dark,
  },
  peakAxis: {
    position: 'relative',
    height: 14,
    // `direction` is set inline so jss-rtl can't flip it.
  },
  peakAxisLabel: {
    position: 'absolute',
    // `transform: translateX(-50%)` is set inline for the same reason.
    fontSize: '0.7rem',
    color: '#888',
  },
}))
